import {
  Box,
  Button,
  Drawer,
  Group,
  Radio,
  Select,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronDown } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SHA256 } from "crypto-js";
import { useGenerateOTPMutation } from "@/services/authAPI";
import {
  useGetSensitiveDebitCardInfoMutation,
  useGetSensitiveCreditCardInfoMutation,
} from "@/services/cardAPI";
import { RESULT_CODE } from "@/constants/enum";
import { GetSensitiveDebitCardInfoResponse } from "@/dto/card/response/CardResponse";

const AUTH_TYPE_CODE: Record<string, number> = {
  SMS_OTP: 1,
  SMART_OTP: 3,
};

type PropData = {
  openedDrawer: boolean;
  closeDrawer: () => void;
  openModal: () => void;
  openedModal?: boolean;
  cardId?: number;
  refId?: string;
  otpId?: string;
  /** JVC-3543: thẻ tín dụng gọi API getSensitiveCreditCardInfo thay vì debit. */
  isCredit?: boolean;
  onSuccess?: (info: GetSensitiveDebitCardInfoResponse) => void;
};

const AuthenticationMode = ({
  openedDrawer,
  closeDrawer,
  openModal,
  openedModal = false,
  cardId,
  refId,
  otpId: initialOtpId,
  isCredit = false,
  onSuccess,
}: PropData) => {
  const { t } = useTranslation();
  const [authentication, setAuthentication] = useState<"1" | "2">("1");
  const [mode, setMode] = useState<"1" | "2">("1");
  const [otp, setOtp] = useState("");
  const [otpId, setOtpId] = useState("");

  const [generateOTP, { isLoading: isGeneratingOtp }] =
    useGenerateOTPMutation();
  const [getSensitiveDebitCardInfo, { isLoading: isFetchingSensitive }] =
    useGetSensitiveDebitCardInfoMutation();
  const [getSensitiveCreditCardInfo, { isLoading: isFetchingSensitiveCredit }] =
    useGetSensitiveCreditCardInfoMutation();

  useEffect(() => {
    if (openedDrawer) {
      setOtp("");
      setOtpId(initialOtpId || "");
      setAuthentication("1");
      setMode("1");
    }
  }, [openedDrawer, initialOtpId]);

  const resolvedAuthType = (): number => {
    if (authentication === "1") return AUTH_TYPE_CODE.SMS_OTP;
    return AUTH_TYPE_CODE.SMART_OTP;
  };

  const handleGetOtp = async () => {
    try {
      const otpType = authentication === "1" ? "SMS_OTP" : "SMART_OTP";
      const res = await generateOTP({
        objectId: refId || String(cardId || ""),
        objectType: "VIEW_CARD",
        otpType,
        phoneNumber: "0987978978",
      }).unwrap();
      if (res.body?.resultCode !== RESULT_CODE.SUCCESS) {
        notifications.show({
          title: t("ERROR"),
          message: res.body?.resultDesc || t("GET_OTP_FAILED"),
          color: "red",
        });
        return;
      }
      setOtpId(res.body?.otpId || "");
      notifications.show({
        title: t("SUCCESS"),
        message: t("OTP_SENT"),
        color: "green",
      });
    } catch {
      notifications.show({
        title: t("ERROR"),
        message: t("CONNECTION_ERROR"),
        color: "red",
      });
    }
  };

  const handleSubmit = async () => {
    if (!cardId || !refId) {
      notifications.show({
        title: t("ERROR"),
        message: t("MISSING_REQUEST_INFO"),
        color: "red",
      });
      return;
    }
    if (!otp || !otpId) {
      notifications.show({
        title: t("ERROR"),
        message: t("PLEASE_REQUEST_AND_ENTER_OTP"),
        color: "red",
      });
      return;
    }
    // Chỉ cho phép OTP đúng 6 chữ số
    if (otp.length !== 6) {
      notifications.show({
        title: t("ERROR"),
        message: t("OTP_MUST_BE_6_DIGITS"),
        color: "red",
      });
      return;
    }
    try {
      // JVC-3543: thẻ tín dụng dùng API sensitive credit, cùng shape request/response
      const fetchSensitive = isCredit
        ? getSensitiveCreditCardInfo
        : getSensitiveDebitCardInfo;
      const res = await fetchSensitive({
        refId,
        cardId,
        authObject: {
          authType: resolvedAuthType(),
          otpId,
          otp: SHA256(otp).toString(),
        },
      }).unwrap();
      if (res.body?.resultCode !== RESULT_CODE.SUCCESS) {
        notifications.show({
          title: t("ERROR"),
          message: res.body?.resultDesc || t("GET_CARD_DETAIL_FAILED"),
          color: "red",
        });
        return;
      }
      onSuccess?.(res.body);
      closeDrawer();
      openModal();
    } catch {
      notifications.show({
        title: t("ERROR"),
        message: t("CONNECTION_ERROR"),
        color: "red",
      });
    }
  };

  // Chỉ nhận số, tối đa 6 chữ số cho OTP
  const handleOtpChange = (value: string) => {
    setOtp(value.replace(/\D/g, "").slice(0, 6));
  };
  // Báo lỗi khi đã nhập nhưng chưa đủ 6 số
  const otpError = otp.length > 0 && otp.length < 6 ? t("OTP_MUST_BE_6_DIGITS") : "";

  return (
    <Drawer
      opened={openedDrawer}
      onClose={closeDrawer}
      title={`${t("SELECT_AUTHENTICATION_MODE")}`}
      overlayProps={{ opacity: 0.0, blur: 0.0 }}
      position="bottom"
      sx={{
        "& .mantine-Drawer-inner": {
          width: "calc(100% - var(--mantine-navbar-width, 0px))",
          right: 0,
        },
        "& .mantine-12c9f7b": {
          right: 0,
          zIndex: 999,
        },
        // Khớp bề rộng + vị trí với khung "Thông tin thẻ" bên trên:
        // trừ padding trang 2 bên (20px + 20px) + cột action phải (280px) + gap (16px) = 336px,
        // lùi trái 20px để canh trùng mép trái khung thẻ.
        // Lưu ý: content của bottom drawer dùng "flex: 0 0 100%" nên phải override flex-basis
        // (set "width" sẽ bị flex-basis đè, không có tác dụng).
        "& .mantine-Drawer-content": {
          flex: "0 0 calc(100% - 336px)",
          marginLeft: "20px",
          background: "#F8F9FB",
        },
        ...(openedModal && { backdropFilter: "blur(5px)" }),
      }}
      styles={{
        header: { borderBottom: "solid 1px #dee2e6" },
        title: {
          fontWeight: 700,
          fontSize: "18px",
          color: "#131618",
        },
        body: {
          height: "75%",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "504px",
            background: "#FFFFFF",
            padding: "24px 32px",
            borderRadius: "8px",
          }}
        >
          <Select
            label={`${t("AUTHENTICATION_MODE")}`}
            value={authentication}
            onChange={(value) => {
              setAuthentication((value as "1" | "2") || "1");
              setOtp("");
              setOtpId("");
            }}
            data={[
              {
                value: "1",
                label: "SMS OTP",
              },
              {
                value: "2",
                label: "SmartOTP",
              },
            ]}
            mb="sm"
            name="accountType"
            rightSection={<IconChevronDown size="1rem" />}
          />
          {authentication === "1" ? (
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <TextInput
                label="OTP"
                placeholder={`${t("INPUT_YOUR_OTP")}`}
                mb="sm"
                value={otp}
                onChange={(e) => handleOtpChange(e.currentTarget.value)}
                error={otpError}
                inputMode="numeric"
                maxLength={6}
                sx={{
                  flex: 1,
                }}
              />
              <Button
                type="button"
                loading={isGeneratingOtp}
                onClick={handleGetOtp}
                sx={() => ({
                  marginTop: "16px",
                  width: "104px",
                  background: "#E5F4FE",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 400,
                  color: "#2A81D0",
                  ":hover": {
                    background: "#E5F4FE",
                    color: "#2A81D0",
                  },
                })}
              >
                {t("GET_OTP")}
              </Button>
            </Box>
          ) : (
            <>
              <Radio.Group
                label={t("MODE")}
                value={mode}
                onChange={(value) => setMode(value as "1" | "2")}
              >
                <Group mt="xs">
                  <Radio value="1" label="SmartOTP Push" />
                  <Radio value="2" label="SmartOTP Code" />
                </Group>
              </Radio.Group>
              {mode === "2" && (
                <Box
                  mt="xs"
                  sx={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                  }}
                >
                  <TextInput
                    label="OTP"
                    placeholder={`${t("INPUT_YOUR_OTP")}`}
                    mb="sm"
                    value={otp}
                    onChange={(e) => handleOtpChange(e.currentTarget.value)}
                    error={otpError}
                    inputMode="numeric"
                    maxLength={6}
                    sx={{
                      flex: 1,
                    }}
                  />
                  <Button
                    type="button"
                    loading={isGeneratingOtp}
                    onClick={handleGetOtp}
                    sx={() => ({
                      marginTop: "16px",
                      width: "104px",
                      background: "#E5F4FE",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                      color: "#2A81D0",
                      ":hover": {
                        background: "#E5F4FE",
                        color: "#2A81D0",
                      },
                    })}
                  >
                    {t("GET_OTP")}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      <Box
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          padding: "10px 16px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          boxShadow: "0px 5px 15px 0px #00000026",
        }}
      >
        <Button
          type="button"
          sx={() => ({
            width: "160px",
            background: "white",
            borderRadius: "8px",
            padding: "8px 10px",
            cursor: "pointer",
            fontWeight: 400,
            color: "#2A81D0",
            ":hover": {
              background: "white",
              color: "#2A81D0",
            },
          })}
          onClick={closeDrawer}
        >
          {t("CANCEL")}
        </Button>
        <Button
          type="button"
          loading={isFetchingSensitive || isFetchingSensitiveCredit}
          sx={() => ({
            width: "160px",
            backgroundColor: "#2A81D0",
            color: "white",
            borderRadius: "8px",
            padding: "8px 10px",
            cursor: "pointer",
            fontWeight: 400,
          })}
          onClick={handleSubmit}
        >
          {t("TRANSFER_STEP_TWO_LABEL")}
        </Button>
      </Box>
    </Drawer>
  );
};
export default AuthenticationMode;
